import {
  alocarVeiculoSchema,
  motivoDeTransicaoSchema,
  ROTULO_DO_STATUS_DE_RESERVA,
  type StatusDaReserva,
} from '@locaveri/shared'
import { LoaderCircle, MoveRight } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ApiError, errorMessage } from '@/lib/api'
import { aplicarErrosDoZod, validarCom } from '@/lib/formulario'

import { ErroDeRealocacao, useRealocar } from './api'

/**
 * Realocação pela agenda — o diálogo que o arrasto (ou o clique na alça) abre.
 *
 * ---------------------------------------------------------------------------
 * O ARRASTO NÃO DECIDE NADA; ELE SÓ PREENCHE O FORMULÁRIO
 * ---------------------------------------------------------------------------
 * Mover a barra de linha é um GESTO, não uma operação: soltar num veículo abre
 * este diálogo com o destino preenchido, e nada acontece sem o motivo do
 * desfazimento e a justificativa da escolha manual — os mesmos campos que a
 * realocação pelo detalhe da reserva exige. É o que mantém a trilha de
 * auditoria da Fase 4 de pé: o carro nunca troca de reserva sem alguém dizer
 * por quê.
 *
 * ---------------------------------------------------------------------------
 * AS DUAS ETAPAS PODEM PARTIR AO MEIO — E A TELA É HONESTA SOBRE ISSO
 * ---------------------------------------------------------------------------
 * Desfazer e alocar são duas requisições. Falha na PRIMEIRA: nada mudou, o
 * erro fica no diálogo e dá para corrigir e tentar de novo. Falha na SEGUNDA,
 * o hook tenta sozinho devolver o carro ORIGINAL, e o desfecho decide a tela:
 * o retorno DEU CERTO, a reserva está exatamente onde estava — o erro fica no
 * diálogo dizendo que o destino recusou mas o carro original foi mantido, e dá
 * para tentar outro destino. O retorno TAMBÉM falhou (o slot original foi
 * tomado no meio-tempo): a reserva ficou sem carro — repetir o formulário
 * chamaria "desfazer" numa reserva sem alocação —, então o diálogo fecha e o
 * toast diz onde a reserva está (raia "a alocar") e onde terminar o serviço.
 * Fingir que nada aconteceu seria pior que o erro.
 */

/** O texto sugerido do motivo — editável, porque o motivo é de quem assina. */
const MOTIVO_SUGERIDO = 'Realocação pela agenda'

const CAMPOS_DO_FORMULARIO = ['justificativa', 'motivo'] as const

/** Um veículo do calendário que pode receber a reserva. */
export interface DestinoDeRealocacao {
  readonly vehicleId: string
  readonly placa: string
  readonly modelo: string
  readonly categoriaNome: string
}

/** O que a barra arrastada (ou clicada) sabe da própria reserva. */
export interface PedidoDeRealocacao {
  readonly reservaId: string
  readonly numero: number
  readonly status: StatusDaReserva
  readonly contaNome: string
  readonly origem: DestinoDeRealocacao
  /** Preenchido quando o gesto foi o arrasto; `null` abre o seletor. */
  readonly destino: DestinoDeRealocacao | null
}

export interface DialogoDeRealocacaoProps {
  /** `null` mantém o diálogo fechado. */
  readonly pedido: PedidoDeRealocacao | null
  /** As linhas do Gantt da MESMA categoria da origem, sem a própria origem. */
  readonly destinos: readonly DestinoDeRealocacao[]
  readonly aoFechar: () => void
}

interface FormularioDeRealocacao {
  destinoId: string
  motivo: string
  justificativa: string
}

interface CorpoDaRealocacaoProps {
  readonly pedido: PedidoDeRealocacao
  readonly destinos: readonly DestinoDeRealocacao[]
  readonly aoFechar: () => void
}

/**
 * O formulário. Componente próprio pelo mesmo motivo dos demais diálogos: o
 * Radix só monta os filhos com o diálogo aberto, então `defaultValues` já
 * nasce com o pedido da vez — sem efeito de ressincronização.
 */
function CorpoDaRealocacao({ pedido, destinos, aoFechar }: CorpoDaRealocacaoProps): ReactElement {
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const realocar = useRealocar()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormularioDeRealocacao>({
    defaultValues: {
      destinoId: pedido.destino?.vehicleId ?? '',
      motivo: MOTIVO_SUGERIDO,
      justificativa: '',
    },
  })

  const destinoFixo = pedido.destino
  const semDestinos = destinoFixo === null && destinos.length === 0
  const ocupado = realocar.isPending
  const status = ROTULO_DO_STATUS_DE_RESERVA[pedido.status]

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const alvo = destinoFixo ?? destinos.find((destino) => destino.vehicleId === valores.destinoId)

    if (alvo === undefined) {
      setError('destinoId', { type: 'validate', message: 'selecione o veículo de destino' })
      return
    }

    const motivoValidado = motivoDeTransicaoSchema.safeParse(valores.motivo)

    if (!motivoValidado.success) {
      setError('motivo', {
        type: 'validate',
        message: motivoValidado.error.issues[0]?.message ?? 'motivo inválido',
      })
      return
    }

    // O MESMO schema que a API aplica no override manual — vehicleId presente
    // exige justificativa não vazia, com as mesmas mensagens.
    const validado = alocarVeiculoSchema.safeParse({
      vehicleId: alvo.vehicleId,
      justificativa: valores.justificativa,
    })

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS_DO_FORMULARIO))
      return
    }

    realocar.mutate(
      {
        reservaId: pedido.reservaId,
        vehicleId: alvo.vehicleId,
        vehicleIdOriginal: pedido.origem.vehicleId,
        justificativa: valores.justificativa.trim(),
        motivoDoDesfazimento: motivoValidado.data,
      },
      {
        onSuccess: (alocacao) => {
          toast.success(`Reserva nº ${String(pedido.numero)} movida para ${alocacao.placa}`, {
            description: `${pedido.origem.placa} ficou livre no período. O calendário foi atualizado.`,
          })
          aoFechar()
        },
        onError: (causa: unknown) => {
          // O destino recusou, mas o retorno automático manteve o carro
          // original — a reserva está exatamente onde estava, então o erro
          // fica no diálogo e dá para tentar outro destino.
          if (causa instanceof ErroDeRealocacao && causa.etapa === 'alocar-com-retorno') {
            setErroGeral(
              `${alvo.placa} não recebeu a reserva: ${errorMessage(causa.causa)} ` +
                `O veículo original (${pedido.origem.placa}) foi mantido — a reserva não ficou sem carro. ` +
                (destinoFixo === null
                  ? 'Se quiser, escolha outro destino e tente de novo.'
                  : 'Você pode tentar de novo, ou cancelar e arrastar para outro veículo.'),
            )
            return
          }

          // O pior caso honesto: desfez, NÃO realocou e o retorno ao carro
          // original também falhou (o slot foi tomado no meio-tempo). A
          // reserva está sem carro na raia "a alocar" — o diálogo fecha
          // (repetir chamaria "desfazer" de novo) e o toast diz onde
          // terminar o serviço.
          if (causa instanceof ErroDeRealocacao && causa.etapa === 'alocar-sem-retorno') {
            toast.error(`A reserva nº ${String(pedido.numero)} ficou sem veículo`, {
              description:
                `A alocação em ${pedido.origem.placa} foi desfeita, mas ${alvo.placa} não recebeu a reserva: ` +
                `${errorMessage(causa.causa)} O retorno automático a ${pedido.origem.placa} também falhou. ` +
                `A reserva está na raia "A alocar" — aloque de novo por lá ou pelo detalhe.`,
              duration: 12_000,
            })
            aoFechar()
            return
          }

          // Falha no desfazer: nada mudou no servidor, dá para tentar de novo.
          const causaReal = causa instanceof ErroDeRealocacao ? causa.causa : causa
          const prefixo =
            ApiError.is(causaReal) && causaReal.status === 409
              ? 'A agenda desta reserva mudou embaixo de você — o calendário foi recarregado. '
              : ''

          setErroGeral(`${prefixo}${errorMessage(causaReal)}`)
        },
      },
    )
  })

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {destinoFixo === null
            ? `Realocar a reserva nº ${String(pedido.numero)}`
            : `Mover a reserva nº ${String(pedido.numero)} de ${pedido.origem.placa} para ${destinoFixo.placa}?`}
        </DialogTitle>
        <DialogDescription>
          {pedido.contaNome} · {status.rotulo} · {pedido.origem.categoriaNome}. A alocação atual é
          desfeita e a nova entra em seguida — motivo e justificativa vão para a trilha de
          auditoria.
        </DialogDescription>
      </DialogHeader>

      <form
        noValidate
        onSubmit={(evento) => {
          void aoEnviar(evento)
        }}
        className="space-y-4"
      >
        {erroGeral === null ? null : (
          <Alert variant="danger">
            <AlertDescription>{erroGeral}</AlertDescription>
          </Alert>
        )}

        {pedido.status === 'RETIRADA_PENDENTE' ? (
          <Alert variant="warning">
            <AlertTitle>Esta reserva já tem contrato</AlertTitle>
            <AlertDescription>
              Ao desfazer a alocação, o contrato desta reserva será cancelado (distrato se assinado)
              — a API cuida disso junto com o desfazimento.
            </AlertDescription>
          </Alert>
        ) : null}

        {destinoFixo === null ? (
          <>
            <Campo
              rotulo="Veículo de destino"
              obrigatorio
              erro={errors.destinoId?.message}
              dica={`Só veículos de ${pedido.origem.categoriaNome} presentes nesta janela do calendário.`}
            >
              {(campo) => (
                <Select
                  {...campo}
                  {...register('destinoId', { required: 'selecione o veículo de destino' })}
                  disabled={ocupado || semDestinos}
                >
                  <option value="">
                    {semDestinos ? 'Nenhum veículo da categoria' : 'Selecione o veículo'}
                  </option>
                  {destinos.map((destino) => (
                    <option key={destino.vehicleId} value={destino.vehicleId}>
                      {destino.placa} · {destino.modelo}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            {semDestinos ? (
              <Alert variant="warning">
                <AlertTitle>Nenhum outro veículo da categoria nesta janela</AlertTitle>
                <AlertDescription>
                  A realocação pela agenda troca a reserva entre carros de{' '}
                  {pedido.origem.categoriaNome}. Amplie a janela ou tire o filtro de categoria — ou
                  aloque pelo detalhe da reserva, que lista os candidatos com score.
                </AlertDescription>
              </Alert>
            ) : null}
          </>
        ) : (
          <div className="rounded-lg border border-border bg-surface p-4">
            <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
              <span>
                {pedido.origem.placa} · {pedido.origem.modelo}
              </span>
              <MoveRight aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
              <span>
                {destinoFixo.placa} · {destinoFixo.modelo}
              </span>
            </p>
            <p className="mt-1 text-xs text-fg-secondary">
              Mesma categoria ({destinoFixo.categoriaNome}). Blindagem, revisão e agenda do destino
              são conferidas pela API ao confirmar.
            </p>
          </div>
        )}

        <Campo
          rotulo="Motivo do desfazimento"
          obrigatorio
          erro={errors.motivo?.message}
          dica="Fica na trilha da alocação desfeita."
        >
          {(campo) => (
            <Input
              {...campo}
              {...register('motivo', { validate: validarCom(motivoDeTransicaoSchema) })}
              disabled={ocupado}
            />
          )}
        </Campo>

        <Campo
          rotulo="Justificativa da nova alocação"
          obrigatorio
          erro={errors.justificativa?.message}
          dica="A escolha do veículo é manual — a justificativa é o que a auditoria lê no lugar do score."
        >
          {(campo) => (
            <Textarea
              {...campo}
              {...register('justificativa', {
                required: 'escolher o veículo à mão exige justificativa',
                validate: (valor: string) => {
                  const texto = valor.trim()

                  if (texto.length < 5) {
                    return 'a justificativa precisa dizer alguma coisa'
                  }

                  return texto.length <= 1000 || 'justificativa muito longa'
                },
              })}
              rows={3}
              disabled={ocupado}
            />
          )}
        </Campo>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
            Cancelar
          </Button>
          <Button type="submit" disabled={ocupado || semDestinos}>
            {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            {ocupado ? 'Movendo…' : 'Mover reserva'}
          </Button>
        </DialogFooter>
      </form>
    </>
  )
}

export function DialogoDeRealocacao({
  pedido,
  destinos,
  aoFechar,
}: DialogoDeRealocacaoProps): ReactElement {
  return (
    <Dialog
      open={pedido !== null}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {pedido === null ? null : (
          <CorpoDaRealocacao pedido={pedido} destinos={destinos} aoFechar={aoFechar} />
        )}
      </DialogContent>
    </Dialog>
  )
}
